import * as React from 'react';
import { useCallback, useMemo } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import { ListItemIcon, ListItemText } from '@mui/material';
import GroupSharpIcon from '@mui/icons-material/GroupSharp';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import PersonSharpIcon from '@mui/icons-material/PersonSharp';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { CollectorScope, CollectorSettingScope } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { useFeatureFlag } from 'modules/common/hooks';
import { isKeyPressed } from 'modules/common/hooks/isKeyPressed';

type useStylesProp = {
  installationScopeSet: Set<string>;
  disableCollectorTenant: boolean;
  scopeSet: Set<string>;
};

const useStyles = makeStyles((theme) => ({
  menuIcon: {
    '&.MuiListItemIcon-root': {
      color: theme.palette.common.black,
      minWidth: theme.spacing(4)
    }
  },

  paper: {
    opacity: 1
  },

  menu: {
    top: theme.spacing(0.5),
    left: ({ installationScopeSet }: useStylesProp) =>
      installationScopeSet.has(CollectorScope.Tenant)
        ? theme.spacing(2.4)
        : theme.spacing(0.5)
  },
  menuListstyle: {
    padding: theme.spacing(0),
    backgroundColor: theme.palette.information.pageBackground
  },
  menuItem: {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.5),
    minWidth: theme.spacing(15.5),
    '&.Mui-focusVisible': {
      background: 'rgba(0, 0, 0, 0.04)'
    }
  },
  MenuDivider: {
    '&.MuiDivider-root': {
      margin: theme.spacing(0)
    }
  },
  forwardIcon: {
    paddingLeft: theme.spacing(1.7),
    color: theme.palette.common.black,
    fontSize: theme.spacing(3.6)
  },
  companyDiv: {
    borderBottom: ({ scopeSet }: useStylesProp) =>
      scopeSet.size > 1 ? `${theme.spacing(0.12)} solid ` : undefined,
    borderBottomWidth: 'thin',
    borderBottomColor: ({ disableCollectorTenant }: useStylesProp) =>
      disableCollectorTenant ? 'rgba(0, 0, 0, 0.3)' : theme.palette.table.border
  }
}));

const useSubMenuStyles = makeStyles((theme) => ({
  menuItem: {
    width: ({ isSubmenuOpen }: { isSubmenuOpen: boolean }) =>
      isSubmenuOpen ? theme.spacing(15) : theme.spacing(0),
    minHeight: theme.spacing(0),
    pointerEvents: 'auto',
    '&.Mui-focusVisible': {
      background: 'rgba(0, 0, 0, 0.04)'
    }
  },

  innerMenu: {
    pointerEvents: 'none',
    '& .MuiList-root': {
      pointerEvents: 'auto',
      padding: theme.spacing(0.4, 0)
    }
  },
  menuListstyle: {
    padding: ({ isSubmenuOpen }: { isSubmenuOpen: boolean }) =>
      isSubmenuOpen ? theme.spacing(0.3) : theme.spacing(0),
    backgroundColor: theme.palette.information.pageBackground,
    minWidth: theme.spacing(0),
    minHeight: theme.spacing(0)
  }
}));

const usePopOverSubMenuStyle = makeStyles((theme) => ({
  paper: {
    minWidth: theme.spacing(0),
    minHeight: theme.spacing(0)
  }
}));

type MenuPopperProp = {
  menuItem: {
    name: string;
    onClick: () => void;
    testId: string;
    toolTip?: string | JSX.Element;
    disabled?: boolean;
  }[];
  anchorPopper: React.RefObject<HTMLLIElement>;
  anchorMenu: HTMLElement | null;
  handleButtonClose: () => void;
  onMouseOverCompany: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseOverUser: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
  scopeSet: Set<string>;
  installationScopeSet: Set<string>;
  connectClick: (scope: any) => void;
  openTenant: boolean;
  menuAnchorUserE1: React.RefObject<HTMLLIElement>;
  onMouseLeaveUser: () => void;
  openUser: boolean;
  showConfigure: boolean;
};
type subMenuProp = {
  anchor: any;
  isSubmenuOpen: boolean;
  onMouseLeave: () => void;
  menuItem: {
    name: string;
    onClick: () => void;
    testId: string;
    toolTip?: string | JSX.Element;
    disabled?: boolean;
  }[];
};
export const SubMenu: React.FC<subMenuProp> = ({
  anchor,
  isSubmenuOpen,
  onMouseLeave,
  menuItem
}: subMenuProp) => {
  const classes = useSubMenuStyles({ isSubmenuOpen });
  const popOverClasses = usePopOverSubMenuStyle();
  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
  const handleMenuKeyDown = useCallback(
    (ev: any) => {
      ev.stopPropagation();
      if (isKeyPressed(ev.key, 'Enter') || isKeyPressed(ev.key, 'ArrowLeft')) {
        ev.preventDefault();
        onMouseLeave();
      }
    },
    [onMouseLeave]
  );
  const menuList = useMemo(() => {
    return { className: classes.menuListstyle };
  }, [classes.menuListstyle]);
  return (
    <>
      {menuItem.length > 0 ? (
        <Menu
          elevation={2}
          TransitionProps={{ onExited: () => anchor.current?.focus() }}
          anchorEl={anchor.current}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          open={isSubmenuOpen}
          MenuListProps={menuList}
          className={classes.innerMenu}
          onClose={onMouseLeave}
          PopoverClasses={popOverClasses}
          onKeyDown={isTiwVpatEnabled ? handleMenuKeyDown : undefined}
        >
          {menuItem.map((i, index) => (
            <Tooltip key={index} title={i?.toolTip || ''} placement={'right'}>
              <MenuItem
                className={classes.menuItem}
                key={i.name}
                data-testid={i.testId}
                onClick={i.onClick}
                disabled={i?.disabled || false}
              >
                {i.name}
              </MenuItem>
            </Tooltip>
          ))}
        </Menu>
      ) : (
        <></>
      )}
    </>
  );
};

export const MenuPopper: React.FC<MenuPopperProp> = ({
  menuItem,
  anchorPopper,
  anchorMenu,
  handleButtonClose,
  onMouseOverCompany,
  onMouseLeave,
  scopeSet,
  installationScopeSet,
  connectClick,
  openTenant,
  menuAnchorUserE1,
  onMouseLeaveUser,
  openUser,
  onMouseOverUser,
  showConfigure
}: MenuPopperProp) => {
  const isSubmenuTenantOpen = useMemo(() => openTenant, [openTenant]);
  const isSubmenuUserOpen = useMemo(() => openUser, [openUser]);

  const isTenantInstalled = React.useMemo(
    () => installationScopeSet.has(CollectorSettingScope.Tenant),
    [installationScopeSet]
  );
  const me = useMeContext();
  const isAdmin = useMemo(() => {
    return me?.permissions.hasCompanyCollectorInstallAccess ? true : false;
  }, [me]);
  const iscollectorTenant = React.useMemo(
    () => scopeSet.has(CollectorSettingScope.Tenant),
    [scopeSet]
  );

  const disableCollectorTenant = useMemo(
    () => scopeSet.has(CollectorSettingScope.Tenant) && !isAdmin,
    [isAdmin, scopeSet]
  );
  const classes = useStyles({
    installationScopeSet,
    disableCollectorTenant,
    scopeSet
  });
  const isUserInstalled = React.useMemo(
    () => installationScopeSet.has(CollectorSettingScope.User),
    [installationScopeSet]
  );
  const iscollectorUser = React.useMemo(
    () => scopeSet.has(CollectorSettingScope.User),
    [scopeSet]
  );

  const onTenantClick = useCallback(() => {
    if (!isTenantInstalled && !isUserInstalled && !showConfigure)
      connectClick(CollectorSettingScope.Tenant);
  }, [connectClick, isTenantInstalled, isUserInstalled, showConfigure]);

  const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');

  const onTenantClickKeyDown = useCallback(
    (e) => {
      if (isKeyPressed(e.key, 'Enter') || isKeyPressed(e.key, 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        onMouseOverCompany(e);
      }
    },
    [onMouseOverCompany]
  );

  const onUserClickKeyDown = useCallback(
    (e) => {
      if (isKeyPressed(e.key, 'Enter') || isKeyPressed(e.key, 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        onMouseOverUser(e);
      }
    },
    [onMouseOverUser]
  );

  const onUserClick = useCallback(() => {
    if (!isTenantInstalled && !isUserInstalled)
      connectClick(CollectorSettingScope.User);
  }, [connectClick, isTenantInstalled, isUserInstalled]);

  return (
    <div>
      <Menu
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        MenuListProps={{ className: classes.menuListstyle }}
        onClose={handleButtonClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        elevation={1}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        className={classes.menu}
        data-testid="modify-menu"
      >
        {(iscollectorTenant || showConfigure) && (
          <MenuItem
            data-testid={`collector-button-${CollectorScope.Tenant}`}
            className={classNames(classes.menuItem, classes.companyDiv)}
            onMouseOver={onMouseOverCompany}
            onMouseLeave={onMouseLeave}
            ref={anchorPopper}
            onClick={onTenantClick}
            disabled={disableCollectorTenant}
            onKeyDown={isTiwVpatEnabled ? onTenantClickKeyDown : undefined}
          >
            <ListItemIcon className={classes.menuIcon}>
              <GroupSharpIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id="collectorType.company" />
            </ListItemText>
            {(installationScopeSet.size > 0 || showConfigure) && (
              <ListItemIcon className={classes.menuIcon}>
                <ArrowForwardIosSharpIcon className={classes.forwardIcon} />
              </ListItemIcon>
            )}

            <SubMenu
              anchor={anchorPopper}
              menuItem={menuItem}
              isSubmenuOpen={isSubmenuTenantOpen}
              onMouseLeave={onMouseLeave}
            />
          </MenuItem>
        )}
        {iscollectorUser && (
          <MenuItem
            data-testid={`collector-button-${CollectorScope.User}`}
            className={classes.menuItem}
            onClick={onUserClick}
            ref={menuAnchorUserE1}
            onMouseLeave={onMouseLeaveUser}
            onMouseOver={onMouseOverUser}
            onKeyDown={isTiwVpatEnabled ? onUserClickKeyDown : undefined}
          >
            <ListItemIcon className={classes.menuIcon}>
              <PersonSharpIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id="collectorType.self" />
            </ListItemText>
            {installationScopeSet.size > 0 && (
              <ListItemIcon className={classes.menuIcon}>
                <ArrowForwardIosSharpIcon className={classes.forwardIcon} />
              </ListItemIcon>
            )}
            <SubMenu
              anchor={menuAnchorUserE1}
              menuItem={menuItem}
              isSubmenuOpen={isSubmenuUserOpen}
              onMouseLeave={onMouseLeaveUser}
            />
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};
